import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import ReviewLayout from '../layouts/ReviewLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import AuthGuard from '../components/AuthGuard';
import useAuth from '../hooks/useAuth';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

function DashboardRedirect() {
  const { user } = useAuth();
  const target = user?.role === 'EXPERT' ? '/dashboard/expert/available' : '/dashboard/home';
  return <Navigate to={target} replace />;
}

export default function Router() {
  return useRoutes([
    { path: '/', element: <Navigate to="/dashboard" replace /> },
    { path: '/login', element: <LoginPage /> },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <DashboardRedirect />, index: true },
        { path: 'home', element: <DashboardPage /> },
        { path: 'essays', element: <AdminEssaysPage /> },
        { path: 'essays/:id', element: <AdminEssayDetailPage /> },
        { path: 'reviews', element: <AdminReviewsPage /> },
        // { path: 'training', element: <AdminTrainingPage /> },
        // { path: 'training/:reviewId', element: <AdminTrainingDetailPage /> },
        { path: 'referrals', element: <AdminReferralsPage /> },
        { path: 'experts', element: <ExpertsPage /> },
        { path: 'experts/:id', element: <ExpertDetailPage /> },
        { path: 'students', element: <StudentsPage /> },
        { path: 'topics', element: <TopicsPage /> },
        { path: 'topic-categories', element: <TopicCategoriesPage /> },
        { path: 'untagged-essays', element: <AdminUntaggedEssaysPage /> },
        { path: 'tariffs', element: <TariffsPage /> },
        { path: 'withdrawals', element: <AdminWithdrawalsPage /> },
        { path: 'applications', element: <AdminApplicationsPage /> },
        { path: 'payments', element: <AdminPaymentsPage /> },
        { path: 'analytics', element: <AdminAnalyticsPage /> },
        { path: 'settings', element: <SettingsPage /> },
        { path: 'ai-review-rules', element: <AiReviewRulesPage /> },
        { path: 'ai-review-rule-categories', element: <AiReviewRuleCategoriesPage /> },
        { path: 'feedbacks', element: <AdminFeedbackPage /> },
        { path: 'courses', element: <CoursesPage /> },
        { path: 'courses/:id', element: <CourseDetailPage /> },
        // Expert routes
        { path: 'expert/available', element: <ExpertAvailableEssaysPage /> },
        { path: 'expert/essays', element: <ExpertEssaysPage /> },
        { path: 'expert/reviewed', element: <ExpertReviewedPage /> },
        { path: 'expert/cards', element: <ExpertCardsPage /> },
        { path: 'expert/profile', element: <ExpertProfilePage /> },
      ],
    },
    {
      path: '/review/:id',
      element: (
        <AuthGuard>
          <ReviewLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <ExpertReviewPage /> },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
  ]);
}

// Pages
const LoginPage = Loadable(lazy(() => import('../pages/LoginPage')));
const DashboardPage = Loadable(lazy(() => import('../pages/DashboardPage')));
const ExpertsPage = Loadable(lazy(() => import('../pages/ExpertsPage')));
const ExpertDetailPage = Loadable(lazy(() => import('../pages/ExpertDetailPage')));
const StudentsPage = Loadable(lazy(() => import('../pages/StudentsPage')));
const TopicsPage = Loadable(lazy(() => import('../pages/TopicsPage')));
const TopicCategoriesPage = Loadable(lazy(() => import('../pages/TopicCategoriesPage')));
const AdminUntaggedEssaysPage = Loadable(lazy(() => import('../pages/AdminUntaggedEssaysPage')));
const TariffsPage = Loadable(lazy(() => import('../pages/TariffsPage')));
const AdminEssaysPage = Loadable(lazy(() => import('../pages/AdminEssaysPage')));
const AdminEssayDetailPage = Loadable(lazy(() => import('../pages/AdminEssayDetailPage')));
const AdminReviewsPage = Loadable(lazy(() => import('../pages/AdminReviewsPage')));
// const AdminTrainingPage = Loadable(lazy(() => import('../pages/AdminTrainingPage')));
// const AdminTrainingDetailPage = Loadable(lazy(() => import('../pages/AdminTrainingDetailPage')));
const AdminReferralsPage = Loadable(lazy(() => import('../pages/AdminReferralsPage')));
const ExpertAvailableEssaysPage = Loadable(lazy(() => import('../pages/ExpertAvailableEssaysPage')));
const ExpertEssaysPage = Loadable(lazy(() => import('../pages/ExpertEssaysPage')));
const ExpertReviewPage = Loadable(lazy(() => import('../pages/ExpertReviewPage')));
const ExpertReviewedPage = Loadable(lazy(() => import('../pages/ExpertReviewedPage')));
const ExpertProfilePage = Loadable(lazy(() => import('../pages/ExpertProfilePage')));
const ExpertCardsPage = Loadable(lazy(() => import('../pages/ExpertCardsPage')));
const AdminWithdrawalsPage = Loadable(lazy(() => import('../pages/AdminWithdrawalsPage')));
const AdminApplicationsPage = Loadable(lazy(() => import('../pages/AdminApplicationsPage')));
const AdminPaymentsPage = Loadable(lazy(() => import('../pages/AdminPaymentsPage')));
const AdminAnalyticsPage = Loadable(lazy(() => import('../pages/AdminAnalyticsPage')));
const SettingsPage = Loadable(lazy(() => import('../pages/SettingsPage')));
const AiReviewRulesPage = Loadable(lazy(() => import('../pages/AiReviewRulesPage')));
const AiReviewRuleCategoriesPage = Loadable(lazy(() => import('../pages/AiReviewRuleCategoriesPage')));
const AdminFeedbackPage = Loadable(lazy(() => import('../pages/AdminFeedbackPage')));
const CoursesPage = Loadable(lazy(() => import('../pages/CoursesPage')));
const CourseDetailPage = Loadable(lazy(() => import('../pages/CourseDetailPage')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
